// services/casosService.js
import axios from 'axios';

// Usar la misma configuración que otros servicios para consistencia
export const API_URL = `${process.env.REACT_APP_SURVEY_API_URL}/api`;

// Instancia de axios configurada para la API de Laravel (datofy-api)
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('token');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores globalmente
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Error en casos API:', error);

    if (error.response?.status === 401) {
      console.warn('🔐 Token expired or invalid in casos service');
    }

    return Promise.reject(error);
  }
);

/**
 * Servicio para gestión de casos
 */
export const casosService = {

  /**
   * Obtener todos los casos de un usuario
   * @param {string} username - Username del usuario
   */
  getCasosByUser: async (username) => {
    try {
      console.log('🔍 Obteniendo casos para usuario:', username);

      const response = await api.get(`/casos/respondent/${username}`);

      console.log('✅ Casos obtenidos exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo casos:', {
        username,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al obtener casos');
    }
  },

  /**
   * Obtener un caso específico
   * @param {number} casoId - ID del caso
   */
  getCaso: async (casoId) => {
    try {
      console.log('🔍 Obteniendo caso:', casoId);

      const response = await api.get(`/casos/${casoId}`);

      console.log('✅ Caso obtenido exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo caso:', {
        casoId,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al obtener caso');
    }
  },

  /**
   * Crear un nuevo caso
   * @param {object} casoData - Datos del caso
   */
  createCaso: async (casoData) => {
    try {
      console.log('🔄 Creando nuevo caso:', casoData);

      const response = await api.post('/casos', casoData);

      console.log('✅ Caso creado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando caso:', {
        casoData,
        status: error.response?.status,
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al crear caso');
    }
  },

  /**
   * Actualizar un caso existente
   * @param {number} casoId - ID del caso
   * @param {object} casoData - Datos actualizados
   */
  updateCaso: async (casoId, casoData) => {
    try {
      console.log('🔄 Actualizando caso:', { casoId, casoData });

      const response = await api.put(`/casos/${casoId}`, casoData);

      console.log('✅ Caso actualizado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando caso:', {
        casoId,
        casoData,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al actualizar caso');
    }
  },

  /**
   * Obtener fichas (survey responses) de un caso
   * @param {number} casoId - ID del caso
   */
  getFichasByCaso: async (casoId) => {
    try {
      console.log('🔍 Obteniendo fichas del caso:', casoId);

      const response = await api.get(`/casos/${casoId}/fichas`);

      console.log('✅ Fichas obtenidas exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo fichas del caso:', {
        casoId,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al obtener fichas del caso');
    }
  },

  /**
   * Actualizar el workflow stage de un caso
   * @param {number} casoId - ID del caso
   * @param {string} newStage - Nuevo stage
   * @param {string} comment - Comentario del cambio
   * @param {number} userId - ID del usuario que hace el cambio
   */
  updateCasoStage: async (casoId, newStage, comment = null, userId = null) => {
    try {
      console.log('🔄 Actualizando stage del caso:', {
        casoId,
        newStage,
        comment,
        userId
      });

      const response = await api.put(`/casos/${casoId}/stage`, {
        new_stage: newStage,
        comment,
        user_id: userId
      });

      console.log('✅ Stage del caso actualizado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando stage del caso:', {
        casoId,
        newStage,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al actualizar el stage del caso');
    }
  },

  /**
   * Cambiar status de un caso (pausar, reanudar, cerrar)
   * @param {number} casoId - ID del caso
   * @param {string} action - Acción (pause, resume, close)
   * @param {string} comment - Comentario
   * @param {number} userId - ID del usuario
   */
  changeCasoStatus: async (casoId, action, comment = null, userId = null) => {
    try {
      console.log('🔄 Cambiando status del caso:', {
        casoId,
        action,
        comment,
        userId
      });

      const response = await api.post(`/casos/${casoId}/status`, {
        action,
        comment,
        user_id: userId
      });

      console.log('✅ Status del caso cambiado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error cambiando status del caso:', {
        casoId,
        action,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al cambiar status del caso');
    }
  },

  /**
   * Eliminar un caso
   * @param {number} casoId - ID del caso
   */
  deleteCaso: async (casoId) => {
    try {
      console.log('🗑️ Eliminando caso:', casoId);

      const response = await api.delete(`/casos/${casoId}`);

      console.log('✅ Caso eliminado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando caso:', {
        casoId,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al eliminar caso');
    }
  },

  /**
   * Obtener survey responses de un usuario (disponibles y asignados)
   * @param {string} username - Username del usuario
   */
  getUserSurveyResponses: async (username) => {
    try {
      console.log('🔍 Obteniendo survey responses del usuario:', username);

      const response = await api.get(`/usuarios/${username}/survey-responses`);

      console.log('✅ Survey responses obtenidos exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo survey responses del usuario:', {
        username,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al obtener survey responses del usuario');
    }
  },

  /**
   * Adjuntar una ficha (survey response) a un caso
   * @param {number} casoId - ID del caso
   * @param {number} surveyResponseId - ID del survey response
   * @param {string} workflowStage - Stage inicial (default: recepcion)
   */
  attachFichaToCaso: async (casoId, surveyResponseId, workflowStage = 'recepcion') => {
    try {
      console.log('🔗 Adjuntando ficha a caso:', {
        casoId,
        surveyResponseId,
        workflowStage
      });

      const response = await api.post(`/casos/${casoId}/attach-ficha`, {
        survey_response_id: surveyResponseId,
        workflow_stage: workflowStage
      });

      console.log('✅ Ficha adjuntada exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error adjuntando ficha a caso:', {
        casoId,
        surveyResponseId,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al adjuntar ficha al caso');
    }
  },

  /**
   * Desadjuntar una ficha de un caso (hacerla disponible)
   * @param {number} casoId - ID del caso
   * @param {number} surveyResponseId - ID del survey response
   */
  detachFichaFromCaso: async (casoId, surveyResponseId) => {
    try {
      console.log('🔗 Desadjuntando ficha del caso:', {
        casoId,
        surveyResponseId
      });

      const response = await api.post(`/casos/${casoId}/detach-ficha`, {
        survey_response_id: surveyResponseId
      });

      console.log('✅ Ficha desadjuntada exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error desadjuntando ficha del caso:', {
        casoId,
        surveyResponseId,
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Error al desadjuntar ficha del caso');
    }
  }
};

/**
 * Constantes del workflow (sincronizadas con Laravel)
 */
const CASO_WORKFLOW_STAGES = {
  recepcion: 'Recepción',
  investigacion: 'Investigación',
  resolucion: 'Resolución',
  cierre: 'Cierre'
};

const CASO_WORKFLOW_STAGE_COLORS = {
  recepcion: '#E3F2FD',      // Azul claro
  investigacion: '#FFF3E0',  // Naranja claro
  resolucion: '#F3E5F5',     // Púrpura claro
  cierre: '#E8F5E8'          // Verde claro
};

const CASO_STATUS_OPTIONS = {
  abierto: 'Abierto',
  cerrado: 'Cerrado',
  pausado: 'Pausado'
};

const CASO_STATUS_COLORS = {
  abierto: 'green',
  cerrado: 'gray',
  pausado: 'orange'
};

/**
 * Utilidades para casos
 */
export const casosUtils = {

  // Constantes disponibles
  CASO_WORKFLOW_STAGES,
  CASO_WORKFLOW_STAGE_COLORS,
  CASO_STATUS_OPTIONS,
  CASO_STATUS_COLORS,

  /**
   * Obtener nombre del stage
   * @param {string} stageKey
   */
  getStageName: (stageKey) => {
    return CASO_WORKFLOW_STAGES[stageKey] || 'Desconocido';
  },

  /**
   * Obtener color del stage
   * @param {string} stageKey
   */
  getStageColor: (stageKey) => {
    return CASO_WORKFLOW_STAGE_COLORS[stageKey] || '#F5F5F5';
  },

  /**
   * Obtener nombre del status
   * @param {string} statusKey
   */
  getStatusName: (statusKey) => {
    return CASO_STATUS_OPTIONS[statusKey] || 'Desconocido';
  },

  /**
   * Obtener color del status
   * @param {string} statusKey
   */
  getStatusColor: (statusKey) => {
    return CASO_STATUS_COLORS[statusKey] || 'gray';
  },

  /**
   * Validar si un stage es válido
   * @param {string} stageKey
   */
  isValidStage: (stageKey) => {
    return Object.keys(CASO_WORKFLOW_STAGES).includes(stageKey);
  },

  /**
   * Obtener siguiente stage en el flujo
   * @param {string} currentStage
   */
  getNextStage: (currentStage) => {
    const stages = Object.keys(CASO_WORKFLOW_STAGES);
    const currentIndex = stages.indexOf(currentStage);

    if (currentIndex === -1 || currentIndex >= stages.length - 1) {
      return null; // No hay siguiente stage
    }

    return stages[currentIndex + 1];
  },

  /**
   * Obtener stage anterior en el flujo
   * @param {string} currentStage
   */
  getPreviousStage: (currentStage) => {
    const stages = Object.keys(CASO_WORKFLOW_STAGES);
    const currentIndex = stages.indexOf(currentStage);

    if (currentIndex <= 0) {
      return null; // No hay stage anterior
    }

    return stages[currentIndex - 1];
  },

  /**
   * Formatear fecha para mostrar
   * @param {string} dateString
   */
  formatDate: (dateString) => {
    if (!dateString) return 'Sin fecha';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  },

  /**
   * Calcular tiempo transcurrido desde una fecha
   * @param {string} dateString
   */
  getTimeAgo: (dateString) => {
    if (!dateString) return 'Sin fecha';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffDays > 0) {
        return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
      } else if (diffHours > 0) {
        return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
      } else if (diffMinutes > 0) {
        return `hace ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
      } else {
        return 'hace un momento';
      }
    } catch (error) {
      return 'Fecha inválida';
    }
  }
};

export default casosService;